import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Avatar,
  MenuItem,
  TableRow,
  TableBody,
  TableCell,
  Switch,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import {
  OpenInNewOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { CustomerSettingsState } from "../../models/models";
import { getCustomer, setError, setSuccess } from "../../customerSettingsSlice";
import { CustomerContact, CustomerContactsState } from "../../../shared/models/customerContactsModels";
import { deleteContact, getContacts, selectContact, setError as setContactError, setSuccess as setContactSuccess } from "../../customerContactsSlice";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import ActionToast from "../../../../shared/components/ActionToast";
import Loader from "../../../../shared/components/Loader";
import TableEmpty from "../../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../../shared/assets/icons/BoxSearchIcon";
import Table from "../../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../../shared/components/SortTableHead";
import ImageBox from "../../../../shared/components/ImageBox";
import TableActionButton from "../../../../shared/components/tables/TableActionButton";
import { NotificationMessage, stringAvatar } from "../../../../shared/models/models";
import { formatAddressString, Address, AddressTypeCode } from "../../../shared/models/customerModels";
import ContactDialog from "../ContactDialog";
import useTranslation from "../../../../shared/hooks/useTranslation";
import SettingsCard from "../shared/SettingsCard";
import chatBotApi from "../../../../shared/chatBot/ChatBotApi";
import { BotAccessResponse } from "../../../../shared/chatBot/models/chatBotModels";
import { updateBotChatSettings } from "../../../../shared/chatBot/chatBotSlice";
import { dt } from "../../../../shared/themes/redesignTokens";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";

const SettingsCompany: React.FC = () => {
  const { customer, loading: customerLoading } = useAppSelector<CustomerSettingsState>(
    (state: any) => state.customerSettings
  );
  const { contacts, contact, loading: contactsLoading, success: contactSuccess, error: contactError } =
    useAppSelector<CustomerContactsState>((state: any) => state.customerContacts);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { translate } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [isContactDialog, setIsContactDialog] = useState(false);

  // MIDAS bot chat toggle state
  const [botSettings, setBotSettings] = useState<BotAccessResponse | null>(null);
  const [botLoading, setBotLoading] = useState(true);
  const [botToggling, setBotToggling] = useState(false);

  // Load bot chat settings on mount
  useEffect(() => {
    let cancelled = false;
    setBotLoading(true);
    chatBotApi
      .checkBotAccess()
      .then((result) => {
        if (!cancelled) {
          setBotSettings(result);
          setBotLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBotSettings(null);
          setBotLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBotToggle = useCallback(async () => {
    if (!botSettings || botToggling) return;
    const newEnabled = !botSettings.enabled;
    setBotToggling(true);
    try {
      const result = await dispatch(updateBotChatSettings(newEnabled)).unwrap();
      setBotSettings(result);
    } catch {
      // Revert on failure - keep the current state
    } finally {
      setBotToggling(false);
    }
  }, [botSettings, botToggling, dispatch]);

  const contactColumns: TableColumnDefinition[] = [
    { title: tCommon("image") },
    { title: tCommon("forms.name") },
    { title: translate("position") },
    { title: translate("dept") },
    { title: tCommon("forms.email") },
    { title: tCommon("forms.phone") },
    { title: translate("actions") },
  ];

  const handleAddContact = () => {
    dispatch(selectContact(null));
    setIsContactDialog(true);
  };

  const handleEditContact = (item: CustomerContact) => {
    setIsContactDialog(true);
    dispatch(selectContact(item));
  };

  const handleDeleteContact = async (item: CustomerContact) => {
    if (item) {
      await dispatch(deleteContact(item.id));
      await dispatch(getContacts());
    }
  };

  useEffect(() => {
    dispatch(getCustomer());
    dispatch(getContacts());
  }, [dispatch]);

  // Find the office/legal address
  const officeAddress = customer?.addresses?.find(
    (a: Address) => a.typeCode?.id === AddressTypeCode.legal || a.typeCode?.id === AddressTypeCode.billing
  );
  const addressString = officeAddress ? formatAddressString(officeAddress) : undefined;

  const tableSx = {
    "& thead th": {
      backgroundColor: dt.headerBg,
      borderBottom: `1px solid ${dt.cardBorder}`,
      fontSize: "11px !important",
      fontWeight: "700 !important",
      textTransform: "uppercase",
      letterSpacing: "1.2px",
      color: "#64748B !important",
      fontFamily: `${dt.font} !important`,
      padding: "14px 16px",
      whiteSpace: "nowrap",
    },
    "& tbody tr": {
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        backgroundColor: dt.rowHover,
        boxShadow: `inset 3px 0 0 0 ${dt.blue}`,
      },
    },
    "& tbody td": {
      fontFamily: `${dt.font} !important`,
      fontSize: "13px",
      padding: "10px 16px",
      borderBottom: `1px solid ${dt.rowBorder}`,
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <ActionToast
        open={!!contactSuccess}
        variant="success"
        message={contactSuccess || ""}
        onClose={() => dispatch(setContactSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!contactError}
        variant="error"
        message={NotificationMessage.error(contactError).message || ""}
        onClose={() => dispatch(setContactError(undefined))}
        duration={6000}
      />

      {/* Company Summary Card */}
      <SettingsCard title={translate("settings:company.title")} animationDelay="0.15s">
        {customerLoading ? (
          <Loader />
        ) : customer ? (
          <Box>
            {/* Company header row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mb: 2.5 }}>
              {customer.logo?.content ? (
                <Box
                  component="img"
                  src={customer.logo.content}
                  alt={customer.name}
                  sx={{
                    width: "56px",
                    height: "56px",
                    borderRadius: dt.radiusSm,
                    objectFit: "cover",
                    border: `1px solid ${dt.cardBorder}`,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "56px",
                    height: "56px",
                    minWidth: "56px",
                    borderRadius: dt.radiusSm,
                    backgroundColor: "#F4F6F9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "18px",
                    color: dt.textSecondary,
                    fontFamily: dt.font,
                    border: `1px solid ${dt.cardBorder}`,
                  }}
                >
                  {(customer.name || "?").charAt(0).toUpperCase()}
                </Box>
              )}
              <Box>
                <Box sx={{ fontSize: "16px", fontWeight: 700, color: dt.textPrimary, fontFamily: dt.font }}>
                  {customer.name}
                </Box>
                {customer.registrationNumber && (
                  <Box sx={{ fontSize: "12px", color: dt.textMuted, fontFamily: dt.font, mt: 0.25 }}>
                    {translate("settings:company.registrationNumber")}: {customer.registrationNumber}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Info grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: "12px",
                mb: 2.5,
              }}
            >
              {customer.vatId && (
                <InfoRow label={translate("settings:company.vatNumber")} value={customer.vatId} />
              )}
              {addressString && (
                <InfoRow label={translate("settings:company.address")} value={addressString} />
              )}
              {customer.website && (
                <InfoRow label={translate("settings:company.website")} value={customer.website} />
              )}
            </Box>

            {/* Edit on Business Network link */}
            <Box
              onClick={() => navigate(`/companies/${customer.id}`)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textLink,
                fontFamily: dt.font,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": { color: dt.blueHover, textDecoration: "underline" },
              }}
            >
              {translate("settings:company.editOnNetwork")}
              <OpenInNewOutlined sx={{ fontSize: "16px" }} />
            </Box>
          </Box>
        ) : null}
      </SettingsCard>

      {/* Features Card - MIDAS AI Assistant toggle */}
      <SettingsCard
        title={translate("settings:company.features")}
        animationDelay="0.2s"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: dt.textPrimary,
                fontFamily: dt.font,
                mb: 0.5,
              }}
            >
              {translate("settings:company.midasTitle")}
            </Box>
            <Box
              sx={{
                fontSize: "13px",
                color: dt.textSecondary,
                fontFamily: dt.font,
                lineHeight: 1.5,
              }}
            >
              {translate("settings:company.midasDescription")}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {botLoading ? (
              <CircularProgress size={20} sx={{ color: dt.blue, mx: 1.5 }} />
            ) : (
              <Switch
                checked={botSettings?.enabled ?? true}
                onChange={handleBotToggle}
                disabled={botToggling || !(botSettings?.globalEnabled ?? true)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: dt.blue,
                    "&:hover": {
                      backgroundColor: dt.blueSoft,
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: dt.blue,
                  },
                }}
              />
            )}
          </Box>
        </Box>
        {botSettings && !botSettings.globalEnabled && (
          <Box
            sx={{
              mt: 1.5,
              fontSize: "12px",
              color: dt.textMuted,
              fontFamily: dt.font,
              fontStyle: "italic",
            }}
          >
            {translate("settings:company.midasGlobalDisabled")}
          </Box>
        )}
      </SettingsCard>

      {/* Contacts Card */}
      <SettingsCard
        title={translate("contactsTab")}
        action={
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddContact}
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: dt.radiusSm,
              padding: "5px 14px",
              color: dt.textSecondary,
              borderColor: dt.cardBorder,
              "&:hover": { borderColor: dt.blue, color: dt.blue, backgroundColor: dt.blueSoft },
            }}
          >
            {translate("addNewContact")}
          </Button>
        }
        animationDelay="0.25s"
      >
        {contactsLoading ? (
          <Loader />
        ) : !contacts?.length ? (
          <TableEmpty iconComponent={BoxSearchIcon} />
        ) : isPhone ? (
          /* Mobile: contact cards */
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "hidden", width: "100%" }}>
            {contacts.map((item, index) => (
              <Box
                key={item.id}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: dt.radiusSm,
                  border: `1px solid ${dt.cardBorder}`,
                  borderLeft: `3px solid ${dt.blue}`,
                  padding: "12px",
                  overflow: "hidden",
                  width: "100%",
                  boxSizing: "border-box",
                  animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 1 }}>
                  {item?.file?.content ? (
                    <ImageBox size="small" src={item.file.content} circle={true} />
                  ) : (
                    <Avatar
                      {...stringAvatar(item.name.toUpperCase())}
                      sx={{
                        width: 36,
                        height: 36,
                        background: "#F4F5F5",
                        color: "#000",
                        fontFamily: dt.font,
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontSize: "13px", fontWeight: 600, color: dt.textPrimary, fontFamily: dt.font }}>
                      {item.name}
                    </Box>
                    {item.position && (
                      <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font }}>
                        {item.position}
                      </Box>
                    )}
                  </Box>
                </Box>
                {item.email && (
                  <Box sx={{ fontSize: "12px", color: dt.textSecondary, fontFamily: dt.font, mb: 0.25 }}>
                    {item.email}
                  </Box>
                )}
                {item.phoneNumber && (
                  <Box sx={{ fontSize: "12px", color: dt.textSecondary, fontFamily: dt.font }}>
                    {item.phoneNumber}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          /* Desktop/Tablet: contacts table */
          <Box sx={{ width: "100%", overflowX: "auto", ...tableSx }}>
            <Table>
              <SortTableHead columns={contactColumns} onSort={() => {}} />
              <TableBody>
                {contacts.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item?.file?.content ? (
                        <ImageBox size="small" src={item.file.content} circle={true} />
                      ) : (
                        <Avatar
                          {...stringAvatar(item.name.toUpperCase())}
                          sx={{
                            width: 40,
                            height: 40,
                            background: "#F4F5F5",
                            color: "#000",
                            fontFamily: dt.font,
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontWeight: 600, color: dt.textPrimary }}>{item.name}</Box>
                    </TableCell>
                    <TableCell>{item.position}</TableCell>
                    <TableCell>{item.department}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.phoneNumber}</TableCell>
                    <TableCell>
                      <TableActionButton stopPropagation={true}>
                        <MenuItem onClick={() => handleEditContact(item)}>
                          <EditOutlined sx={{ mr: 1, fontSize: "18px" }} />
                          {tCommon("buttons.edit")}
                        </MenuItem>
                        <PermissionGuard module={Modules.Settings} level={PermissionLevel.Admin}>
                          <MenuItem
                            onClick={() =>
                              confirm({
                                title: translate("deleteContact"),
                                text: translate("deleteContactText", { name: item.name }),
                                confirmButton: translate("deleteContactConfirm"),
                                closeButton: translate("deleteContactCancel"),
                                severity: "error",
                                onConfirm: () => handleDeleteContact(item),
                              })
                            }
                          >
                            <DeleteOutlined sx={{ mr: 1, fontSize: "18px" }} />
                            {tCommon("buttons.delete")}
                          </MenuItem>
                        </PermissionGuard>
                      </TableActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </SettingsCard>

      <ContactDialog
        key={`contact-dialog-${isContactDialog}`}
        open={isContactDialog}
        onClose={() => setIsContactDialog(false)}
        contact={contact}
      />
    </Box>
  );
};

/* Helper component for info rows */
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "1.2px",
        color: "#6B7280",
        fontFamily: 'var(--font-sans)',
        mb: "4px",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "13px",
        fontWeight: 500,
        color: "#1a1a2e",
        fontFamily: 'var(--font-sans)',
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Box>
  </Box>
);

export default SettingsCompany;
